// Typeable combobox-style picker. Accepts a list of options ({ value, label })
// AND free-text input that gets passed through as-is. The backend parameterizes
// on save, so whatever the user types lands as a slug.
//
// When a value is set it renders as a removable pill. Click the × to clear and
// reveal the input again.
import React, { useEffect, useRef, useState } from 'react'
import { XIcon } from '@heroicons/react/solid'

const LabelPicker = ({ value, options, onChange, placeholder = 'Type or pick a label…' }) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  // Click outside closes the dropdown without selecting.
  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()) || o.value.toLowerCase().includes(query.toLowerCase()))
    : options

  // If the user typed something that doesn't match any option exactly, offer
  // "use as-is" as a synthetic top result.
  const trimmed = query.trim()
  const exactMatch = options.find(o => o.value === trimmed || o.label.toLowerCase() === trimmed.toLowerCase())
  const showCustomOption = trimmed && !exactMatch

  const pick = (v) => {
    onChange(v)
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[0]) pick(filtered[0].value)
      else if (trimmed) pick(trimmed)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Pill view when a value is chosen.
  if (value) {
    const matched = options.find(o => o.value === value)
    return (
      <span className='inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-clcpurple/10 text-clcpurple text-sm font-medium border border-clcpurple/30'>
        {matched ? matched.label : value}
        <button
          type='button'
          aria-label='Remove label'
          onClick={() => onChange('')}
          className='inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-clcpurple/20'
        >
          <XIcon className='h-3.5 w-3.5' />
        </button>
      </span>
    )
  }

  return (
    <div ref={containerRef} className='relative'>
      <input
        type='text'
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className='block w-full sm:max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md text-sm focus:ring-clcpurple focus:border-clcpurple'
      />
      {open && (filtered.length > 0 || showCustomOption) && (
        <ul className='absolute z-10 mt-1 w-full sm:max-w-xs max-h-60 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg text-sm'>
          {showCustomOption && (
            <li>
              <button
                type='button'
                onClick={() => pick(trimmed)}
                className='block w-full text-left px-3 py-2 hover:bg-clcpurple/5 border-b border-gray-100 dark:border-gray-700'
              >
                Use <span className='font-mono'>{trimmed}</span> <span className='text-xs text-gray-500'>(custom)</span>
              </button>
            </li>
          )}
          {filtered.map(o => (
            <li key={o.value}>
              <button
                type='button'
                onClick={() => pick(o.value)}
                className='block w-full text-left px-3 py-2 hover:bg-clcpurple/5'
              >
                {o.label} <span className='ml-1 text-xs text-gray-500'>{o.value}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LabelPicker
